import {
	ChangeDetectionStrategy,
	Component,
	computed,
	inject,
	input,
	OnInit,
} from '@angular/core';
import { fade, openClose } from '@core/animations/animations';
import {
	buildEmploymentAddressData,
	buildEmploymentStatusData,
} from '@globalUtils/clients/clients';
import {
	EmploymentStatus,
	EmploymentStatusData,
	PersonClient,
} from '@models/clients';
import { UntilDestroy } from '@ngneat/until-destroy';
import { DropdownOptions } from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { ClientsDispatch } from 'src/app/shared/state/clients';
import { TestHooks } from './employer-details.hooks';

@UntilDestroy()
@Component({
	selector: 'app-employer-details',
	templateUrl: './employer-details.component.html',
	animations: [fade(), openClose()],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class EmployerDetailsComponent implements OnInit {
	testHooks = TestHooks;
	client_ = input<PersonClient | null>(null);
	form: EmploymentStatusData = {
		employmentStatus: '',
		employmentOccupation: null,
		employmentCompany: null,
		employmentOther: null,
		employmentAddress: {
			street: '',
			unit: '',
			poBox: '',
			city: '',
			postcode: '',
			province: '',
			country: '',
		},
	};
	address_ = computed(() => this.form.employmentAddress);
	employmentStatusEnum = EmploymentStatus;
	employmentStatusOptions: DropdownOptions<string> = [
		{ value: '', name: 'Select option' },
		{
			value: EmploymentStatus.EMPLOYED,
			name: 'Employed (Full-time or Part-time)',
		},
		{ value: EmploymentStatus.UNEMPLOYED, name: 'Unemployed' },
		{ value: EmploymentStatus.SELF_EMPLOYED, name: 'Self-Employed' },
		{ value: EmploymentStatus.RETIRED, name: 'Retired' },
		{ value: EmploymentStatus.STUDENT, name: 'Student' },
		{ value: EmploymentStatus.HOMEMAKER, name: 'Homemaker' },
		{ value: EmploymentStatus.OTHER, name: 'Other' },
	];
	private clientsDispatch = inject(ClientsDispatch);

	ngOnInit(): void {
		if (this.client_()?.employmentStatus) {
			this.form = this.client_()?.employmentStatus;
		}
	}

	updateEmploymentStatus(value: string, field: string): void {
		if (field === 'employmentStatus' && value !== EmploymentStatus.EMPLOYED) {
			this.resetEmploymentStatusEmployed();
		}

		if (field === 'employmentStatus' && value !== EmploymentStatus.OTHER) {
			this.form.employmentOther = null;
		}

		const payload = buildEmploymentStatusData(
			this.client_()?.employmentStatus,
			field,
			value,
		);

		this.updateEmploymentStatusData(payload);
	}

	updateEmploymentAddress(value: string, field: string): void {
		const address = buildEmploymentAddressData(
			this.client_()?.employmentStatus?.employmentAddress,
			field,
			value,
		);

		const payload = buildEmploymentStatusData(
			this.client_()?.employmentStatus,
			'employmentAddress',
			address,
		);

		this.updateEmploymentStatusData(payload);
	}

	updateEmploymentStatusData(data: EmploymentStatusData): void {
		if (!this.client_()?.id) return;

		this.clientsDispatch.OverwritePersonClientFields(
			this.client_().id,
			'employmentStatus',
			data,
		);
	}

	resetEmploymentStatusEmployed(): void {
		this.form.employmentOccupation = null;
		this.form.employmentCompany = null;
		this.form.employmentAddress = {
			street: '',
			unit: '',
			poBox: '',
			city: '',
			postcode: '',
			province: '',
			country: '',
		};
	}
}
